import React, { useState } from "react";

/**
 * CollapsiblePanel — a panel that starts closed and remembers.
 *
 * The editor hub grew three panels that each want a screen of their own
 * (Impressions, Who's using this profile, and next Interviews). Closed, a
 * panel is one line: title, a count, a chevron — so the hub reads as a list
 * of things to open rather than a page to scroll.
 *
 * Every card is closed when a client is opened (David, Oct 11). While you
 * work on that client in this tab, the cards you opened stay open across
 * reloads (sessionStorage, keyed by `scope` — the client id); a new tab or
 * another client starts closed again. Nothing breaks if storage is blocked.
 */
export default function CollapsiblePanel({ id, title, count = null, badge = null, defaultOpen = false, scope = "", children }) {
  const key = `cg.panel.${scope}.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = window.sessionStorage.getItem(key);
      return v === null ? defaultOpen : v === "1";
    } catch { return defaultOpen; }
  });
  function toggle() {
    const next = !open;
    setOpen(next);
    try { window.sessionStorage.setItem(key, next ? "1" : "0"); } catch { /* fine */ }
  }
  const bodyId = `panel-${id}`;
  return (
    <section style={s.panel}>
      <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId} style={s.head}>
        <span style={s.title}>{title}</span>
        {count !== null && <span style={s.count}>{count}</span>}
        {badge}
        <span style={{ ...s.chev, transform: open ? "rotate(90deg)" : "none" }} aria-hidden="true">›</span>
      </button>
      {open && <div id={bodyId} style={s.body}>{children}</div>}
    </section>
  );
}

const s = {
  panel: { border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", background: "var(--color-surface)", overflow: "hidden" },
  head:  { width: "100%", display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-4) var(--space-6)", background: "none", border: "none", cursor: "pointer", font: "inherit", textAlign: "left", color: "var(--color-text-primary)" },
  title: { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", flex: 1 },
  count: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "var(--color-bg-subtle)", borderRadius: 999, padding: "2px 9px" },
  chev:  { fontSize: 20, color: "var(--color-text-tertiary)", transition: "transform 120ms ease", display: "inline-block", width: 14 },
  body:  { padding: "0 var(--space-6) var(--space-6)" },
};
